import { CodeView } from '@amzn/awsui-code-view';
import { CopyToClipboard, FlashbarProps } from "@amzn/awsui-components-react";
import { Errors, PosturePayload } from '@amzn/siteconnect-idp-posture-assessment/dist/lib/models';
import { returnCurrentPosturePayload as realReturnCurrentPosturePayload, returnPosturePayload as realReturnPosturePayload } from '@amzn/siteconnect-idp-posture-assessment/dist/lib/network';
import axios, { AxiosRequestConfig } from "axios";
import MockAdapter from "axios-mock-adapter";
import { useState } from "react";
import { ACCOUNT_MAPPINGS } from "./constants";

export const axiosInstance = axios.create();

export const getPostureResult = async (
  postureData: string,
  userId: string | undefined
) => {
  const reqConfig: AxiosRequestConfig = {
    params: {
      user_posture_info: postureData,
      user_id: userId ?? "posturetest-user",
    },
  };

  const hostname = window?.location.hostname;
  const apiDomain = ACCOUNT_MAPPINGS.get(hostname);

  return await axiosInstance.get(`https://${apiDomain}`, reqConfig);
};

export const mockPosturePayload: PosturePayload = {
  osName: "Windows",
  hostname: "mock-test-machine",
  os: {
    name: "windows",
    version: {
      minor: 0,
      patch: 22631,
      major: 10
    },
    sku: 4
  },
  privateIp: "192.168.1.100",
  publicIp: "203.0.113.1",
  timestamp: new Date().toISOString(),
  firewall: {
    name: "Windows Security",
    productVersion: null,
    installed: true,
    enabled: true
  },
  antivirus: [{
    name: "Windows Defender",
    productVersion: "4.18.24070.5",
    installed: true,
    enabled: true
  }],
  webServerVersion: {
    major: "0",
    minor: "2",
    patch: "5"
  },
  latestWebServerVersion: {
    major: "1",
    minor: "0",
    patch: "0"
  },
  awsVpnClientVersion: "4.1.0"
};

export const returnPosturePayload = async (): Promise<PosturePayload | Errors> => {
  if (process.env.REACT_APP_ENV_VAR === "mock") {
    return new Promise((resolve) => {
      setTimeout(() => resolve(mockPosturePayload), 100);
    });
  }
  return realReturnPosturePayload();
};

export const returnCurrentPosturePayload = async (): Promise<PosturePayload | Errors> => {
  if (process.env.REACT_APP_ENV_VAR === "mock") {
    return new Promise((resolve) => {
      setTimeout(() => resolve(mockPosturePayload), 100);
    });
  }
  return realReturnCurrentPosturePayload();
};

export const mockApi = () => {
  const mockAdapter = new MockAdapter(axiosInstance, { delayResponse: 100 });

  // if you want to edit the posture response do it here manually - this
  // is for quick feedback in the mock server
  mockAdapter.onGet().reply(() => [200, { result: true, reason: {} }]);
  return mockAdapter;
};

export const useFlashbarMessages = (): [
  FlashbarProps.MessageDefinition[],
  (content: string, type: FlashbarProps.Type) => void
] => {
  const [flashbarMessages, setFlashbarMessages] = useState<
    FlashbarProps.MessageDefinition[]
  >([]);

  const createFlashbarMessage = (content: string, type: FlashbarProps.Type) => {
    const now = Date.now();
    setFlashbarMessages(
      flashbarMessages.concat([
        {
          type: type,
          dismissible: true,
          dismissLabel: "Dismiss message",
          onDismiss: () =>
            setFlashbarMessages((prevState) =>
              prevState.filter((message) => message.id !== now.toString())
            ),
          content: content,
          id: now.toString(),
        },
      ])
    );
  };

  return [flashbarMessages, createFlashbarMessage];
};

export const CodeBlockWithCopy = ({ code }:{code: string}) => (
  <CodeView
    content={code}
    actions={
      <CopyToClipboard
        popoverRenderWithPortal
        variant='button'
        copyButtonText='Copy Code'
        copySuccessText='Code copied'
        copyErrorText='Failed to copy'
        textToCopy={code}
      />
    }
  />
)
